// src/services/jewelleryPlan/redemptionService.js
// ENHANCED VERSION - Adds missing API methods for complete workflow

import api from "../api";
import { JEWELLERY_API_ENDPOINTS } from "../../constants/jewellery.constant";

const replacePathParams = (path, params) => {
  let result = path;
  Object.keys(params).forEach((key) => {
    result = result.replace(`:${key}`, params[key]);
  });
  return result;
};

const redemptionService = {
  // ==========================================
  // EXISTING METHODS (Keep as-is)
  // ==========================================

  // Get all redemptions
  getAllRedemptions: async (params = {}) => {
    const response = await api.get(
      JEWELLERY_API_ENDPOINTS.GET_ALL_REDEMPTIONS,
      { params }
    );
    return response.data;
  },

  // Get redemption by ID
  getRedemptionById: async (redemptionId) => {
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.GET_REDEMPTION_BY_ID,
      { id: redemptionId }
    );
    const response = await api.get(url);
    return response.data;
  },

  // Create redemption from quote
  createRedemption: async (data) => {
    const response = await api.post(
      JEWELLERY_API_ENDPOINTS.CREATE_REDEMPTION,
      data
    );
    return response.data;
  },

  // Approve redemption
  approveRedemption: async (redemptionId, data = {}) => {
    const url = replacePathParams(JEWELLERY_API_ENDPOINTS.APPROVE_REDEMPTION, {
      id: redemptionId,
    });
    const response = await api.patch(url, data);
    return response.data;
  },

  // Cancel redemption
  cancelRedemption: async (redemptionId, data) => {
    const url = replacePathParams(JEWELLERY_API_ENDPOINTS.CANCEL_REDEMPTION, {
      id: redemptionId,
    });
    const response = await api.patch(url, data);
    return response.data;
  },

  // Get customer redemptions
  getCustomerRedemptions: async (userId, filters = {}) => {
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.GET_CUSTOMER_REDEMPTIONS,
      { userId }
    );
    const response = await api.get(url, { params: filters });
    return response.data;
  },

  // ==========================================
  // NEW METHODS FOR COMPLETE WORKFLOW
  // ==========================================

  /**
   * Reject redemption (approval rejection, not cancellation)
   * @param {string} redemptionId
   * @param {string} reason - Required rejection reason
   */
  rejectRedemption: async (redemptionId, reason) => {
    // You'll need to add this endpoint to your constants
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.REJECT_REDEMPTION || `/redemptions/:id/reject`,
      { id: redemptionId }
    );
    const response = await api.patch(url, { reason });
    return response.data;
  },

  /**
   * Update payment details for redemption
   * @param {string} redemptionId
   * @param {Object} paymentDetails - { method, reference, transactionId, paidAmount }
   */
  updatePayment: async (redemptionId, paymentDetails) => {
    // You'll need to add this endpoint to your constants
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.UPDATE_PAYMENT || `/redemptions/:id/payment`,
      { id: redemptionId }
    );
    const response = await api.patch(url, paymentDetails);
    return response.data;
  },

  /**
   * Complete redemption (final step)
   * @param {string} redemptionId
   * @param {Object} completionData - { invoiceNumber?, notes? }
   */
  completeRedemption: async (redemptionId, completionData = {}) => {
    // You'll need to add this endpoint to your constants
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.COMPLETE_REDEMPTION ||
        `/redemptions/:id/complete`,
      { id: redemptionId }
    );
    const response = await api.patch(url, completionData);
    return response.data;
  },

  /**
   * Get pending approvals
   * @param {string} branchId - Optional branch filter
   */
  getPendingApprovals: async (branchId) => {
    const params = branchId ? { branchId } : {};
    const response = await api.get(
      JEWELLERY_API_ENDPOINTS.GET_PENDING_APPROVALS || `/redemptions/pending`,
      { params }
    );
    return response.data;
  },

  /**
   * Get redemption statistics
   * @param {Object} filters - { branchId?, startDate?, endDate? }
   */
  getStats: async (filters = {}) => {
    const response = await api.get(
      JEWELLERY_API_ENDPOINTS.GET_REDEMPTION_STATS || `/redemptions/stats`,
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get plan redemptions
   * @param {string} planId
   * @param {Object} filters
   */
  getPlanRedemptions: async (planId, filters = {}) => {
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.GET_PLAN_REDEMPTIONS ||
        `/redemptions/plan/:planId`,
      { planId }
    );
    const response = await api.get(url, { params: filters });
    return response.data;
  },

  /**
   * Generate invoice for redemption
   * @param {string} redemptionId
   */
  generateInvoice: async (redemptionId) => {
    const url = replacePathParams(
      JEWELLERY_API_ENDPOINTS.GENERATE_INVOICE || `/redemptions/:id/invoice`,
      { id: redemptionId }
    );
    const response = await api.post(url);
    return response.data;
  },
};

export default redemptionService;
